import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { twMerge } from 'tailwind-merge';
import { useForceRender } from '@client/hooks/useForceRender';

export interface MarkdownTextareaRef {
    setInitialContent: (content: string) => void;
    getContent: () => string;
    isDirty: () => boolean;
    focus: () => void;
}

export type MarkdownTextareaChangeEvent = {
    event: React.FormEvent<HTMLTextAreaElement> | React.FocusEvent<HTMLTextAreaElement>;
    content: string;
    isDirty: boolean;
    isBlur: boolean;
};

/**
 * 마크다운 텍스트 에디터에서 Textarea 영역
 * - 자동으로 늘어나고 상태를 가져올 수 있다
 */
export const MarkdownTextarea = observer(
    ({
        ref,
        id,
        minRows,
        maxRows,
        onChange,
        onEnter,
        onEsc,
        className,
        disabled,
        placeholder,
    }: {
        /**
         * 에디터는 설정값을 props 로 넘기면 관리가 애매하므로 명시적으로 ref 로 처리한다
         */
        ref?: React.Ref<MarkdownTextareaRef>;

        id?: string;
        minRows?: number;
        maxRows?: number;
        /**
         * - isDirty: 초기값과 실제 값이 다른지 여부 (초기값은 setInitialContent 호출 시 설정)
         * - isBlur: blur 에 의한 이벤트인지 여부 (input 직후에 blur 이벤트가 발생해도 onChange 가 실행됨)
         */
        onChange?: (event: MarkdownTextareaChangeEvent) => void;
        onEnter?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
        onEsc?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
        className?: string;
        disabled?: boolean;
        placeholder?: string;
    }) => {
        const forceRender = useForceRender();
        const textareaRef = useRef<HTMLTextAreaElement>(null);

        const initialContent = useRef('');
        const currentContent = useRef(''); // 실시간 변경 내용 추적을 위함

        const isDirty = useCallback(() => {
            return initialContent.current !== currentContent.current;
        }, []);

        /**
         * 실시간 변경 내용을 추적하기 위해 실제 값을 레퍼런스에 두고 본문이 변경될 때 강제로 렌더링을 호출한다
         */
        const updateContent = useCallback(
            (newContent: string, force = false) => {
                const hasChanged = newContent !== currentContent.current;

                // [순서 주의] 먼저 값을 업데이트 하고 나서 렌더링을 호출한다
                currentContent.current = newContent;

                // isDirty() 는 initialContent 와의 비교라 값이 원래대로 돌아올 때 false가 됨
                // 실제 값 변경 여부(hasChanged)로 렌더링 조건을 판단한다
                if (hasChanged || force) {
                    forceRender();
                    onChange?.({
                        event: {
                            target: {
                                value: newContent,
                            } as HTMLTextAreaElement,
                        } as unknown as React.FormEvent<HTMLTextAreaElement>,
                        content: newContent,
                        isDirty: isDirty(),
                        isBlur: false,
                    });
                }
            },
            [isDirty, forceRender],
        );

        useImperativeHandle(ref, () => {
            return {
                setInitialContent: (content = '') => {
                    initialContent.current = content;
                    updateContent(content, true);
                },
                getContent: () => {
                    return currentContent.current;
                },
                isDirty,
                /**
                 * 포커스 및 커서를 맨 뒤로 이동
                 * - focus() 만 호출하면 커서가 맨 앞으로 가므로 setSelectionRange 로 보정
                 */
                focus: () => {
                    const el = textareaRef.current;
                    if (!el) {
                        return;
                    }
                    el.focus();
                    const length = el.value.length;
                    el.setSelectionRange(length, length);
                },
            };
        }, []);

        return (
            <TextareaAutosize
                ref={textareaRef}
                id={id}
                className={twMerge(
                    'textarea textarea-bordered min-h-0 w-full resize-none',
                    className,
                )}
                autoCorrect="off"
                minRows={minRows}
                maxRows={maxRows}
                autoCapitalize="off"
                spellCheck={false}
                disabled={disabled}
                value={currentContent.current}
                placeholder={placeholder}
                onInput={(e) => {
                    const newContent = (e.target as HTMLTextAreaElement).value;
                    updateContent(newContent);
                }}
                onKeyDown={(e) => {
                    if (e.nativeEvent.isComposing) {
                        return;
                    }

                    if (e.key === 'Enter') {
                        onEnter?.(e);
                    }

                    if (e.key === 'Escape') {
                        onEsc?.(e);
                    }
                }}
                onBlur={(e) => {
                    onChange?.({
                        event: e,
                        content: currentContent.current,
                        isDirty: isDirty(),
                        isBlur: true,
                    });
                }}
            />
        );
    },
);
